import React from "react";
import { Link, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { Calendar, MapPin, User } from "lucide-react"; // changed Users → User since it's single attendee
import { useTicketById } from "../lib/api/ticket";
import TicketPreviewSkeletonSimple from "../components/tickets/TicketPreviewSkeleton";

function slugToText(slug: string) {
  return slug
    .replace(/[-_]/g, " ")
    .replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
}

export default function TicketPreview() {
  const { orderId } = useParams(); // use :orderId in your route
  const { data, isLoading, error } = useTicketById(orderId || null);

  if (isLoading) {
    return <TicketPreviewSkeletonSimple />;
  }

  console.log(error);

  if (error || !data) {
    return (
      <section className="bg-gradient-to-b from-[#f1f1f1] to-[#F5F3F0] flex items-center justify-center py-16 px-6">
        <div className="max-w-5xl w-full bg-white/95 backdrop-blur-md border-4 border-red-300 shadow-2xl p-10 text-center">
          <h1 className="text-3xl font-serif text-red-600 mb-4">
            Error Loading Ticket
          </h1>
          <p className="text-gray-700">
            {error?.response?.data?.message ||
              "Unable to load ticket details. Please try again."}
          </p>
        </div>
      </section>
    );
  }

  // Now data.data is the Ticket
  const ticket = data.data;

  // You don’t have event details in ticket, so these are static for now
  const eventDetails = {
    name: "A Tailored Taste: Fine Dining with Style",
    date: "06 December, 2025",
    time: "6:30 PM – 11:00 PM",
    venue: "Fortnum & Mason, 181 Piccadilly, St. James's, London W1A 1ER",
  };

  const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;

  return (
    <section className="min-h-screen bg-gradient-to-b from-[#f1f1f1] to-[#F5F3F0] flex items-center justify-center py-16 px-6">
      <div className="max-w-5xl w-full bg-white/95 backdrop-blur-md border-4 border-[#B9A68D] shadow-2xl overflow-hidden mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Ticket Details */}
          <div className="py-10 px-5 md:px-10 flex flex-col justify-between bg-gradient-to-br from-[#F5F3F0]/60 to-[#B9A68D]/20">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif text-[#4E5C36] mb-3">
                {eventDetails.name}
              </h1>
              <p className="text-lg font-medium text-gray-700 italic mb-6">
                {slugToText(ticket.tier)} Ticket
              </p>

              <div className="space-y-3 text-gray-800">
                <p className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#4E5C36]" />
                  {eventDetails.date} · {eventDetails.time}
                </p>
                <p className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-[#4E5C36]" />
                  {eventDetails.venue}
                </p>

                {/* Attendee */}
                <div className="flex items-start gap-2">
                  <User className="h-5 w-5 text-[#4E5C36]" />
                  <div>
                    <p className="text-gray-700">
                      <span className="font-medium">
                        {`${ticket.attendee.firstName} ${ticket.attendee.lastName}`}
                      </span>{" "}
                      ({ticket.attendee.email})
                    </p>
                    {ticket.attendee.dietaryRequirements && (
                      <p className="text-sm text-gray-500">
                        Dietary: {ticket.attendee.dietaryRequirements}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <p className="text-lg font-semibold text-gray-900">
                  Ticket Price: £
                  {(
                    parseFloat(ticket.orderId.amountTotal) /
                    ticket.orderId.quantity /
                    100
                  ).toFixed(2)}
                </p>
                {/* <p className="text-xl font-bold text-[#4E5C36]">
                Total Paid: £{order.amountTotal}
              </p> */}
                {/* <p
                className={`text-sm font-medium mt-1 ${
                  order.paymentStatus === "paid"
                    ? "text-green-600"
                    : "text-yellow-600"
                }`}
              >
                Status:{" "}
                {order.paymentStatus.charAt(0).toUpperCase() +
                  order.paymentStatus.slice(1)}
              </p> */}
              </div>
            </div>

            <div className="mt-8">
              <p className="text-sm text-gray-500">Ticket ID: {ticket._id}</p>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="flex flex-col items-center justify-center p-10 bg-white">
            <Link to={`${FRONTEND_URL}/ticket/${orderId}`}>
              <div className="bg-white p-6 shadow-md border border-gray-100">
                <QRCode
                  value={`${FRONTEND_URL}/ticket/${orderId}`}
                  size={220}
                  bgColor="#ffffff"
                  fgColor="#4E5C36"
                />
              </div>
            </Link>
            <p className="text-gray-600 text-sm mt-6 text-center max-w-xs">
              Please present this QR code at the entrance for verification.
            </p>
            <p className="text-gray-500 text-xs mt-2">
              Issued on:{" "}
              {new Date(ticket.createdAt).toLocaleDateString("en-GB")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
